/** @jsxImportSource @emotion/react */
import { ReactNode, useCallback, useMemo, useState } from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Popover from 'react-bootstrap/Popover'
import moment from 'moment'

import { useLazyGetSmsMessagesQuery } from 'store/services/labOrder'
import { Message } from 'store/types'

interface LazySmsPopoverProps {
  orderId: number
  children: ReactNode
}

export const LazySmsPopover = ({ orderId, children }: LazySmsPopoverProps) => {
  const [trigger, { data, isFetching, isUninitialized }] = useLazyGetSmsMessagesQuery()
  const [loaded, setLoaded] = useState(false)

  const onShow = useCallback(() => {
    if (!loaded && (isUninitialized || !data)) {
      trigger(orderId)
      setLoaded(true)
    }
  }, [loaded, isUninitialized, data, orderId, trigger])

  const content = useMemo(() => {
    if (isFetching || !data) {
      return (
        <div className="p-2">
          <small>Loading…</small>
        </div>
      )
    }

    const messages: Message[] = data || []
    return (
      <div className="p-2">
        {messages.slice(0, 5).map((m) => (
          <p key={m.uuid} className="p-2 mb-3">
            <strong>{m.sentBy === 'patient' ? 'Patient' : 'LAB'}: </strong>
            {moment(m.createdAt).format('MM/DD/YYYY HH:mm a')}:<br /> {m.text}
          </p>
        ))}
      </div>
    )
  }, [data, isFetching])

  const popover = (
    <Popover>
      <Popover.Header as="h3">Recent SMS Messages</Popover.Header>
      {content}
    </Popover>
  )

  return (
    <OverlayTrigger trigger={["hover", "focus"]} placement="left" overlay={popover}>
      <span onMouseEnter={onShow} onFocus={onShow} style={{ display: 'inline-flex' }}>
        {children}
      </span>
    </OverlayTrigger>
  )
}

